import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface NotificationContextType {
  notifications: PopupNotification[];
  removeNotification: (id: string) => void;
}

interface PopupNotification {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  timestamp: Date;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const GlobalNotificationProvider: React.FC<{ children: React.ReactNode; userEmail?: string | null }> = ({ children, userEmail }) => {
  const [notifications, setNotifications] = useState<PopupNotification[]>([]);
  const [hasNotificationPermission, setHasNotificationPermission] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const navigate = useNavigate();

  // Request notification permission and set up user interaction detection
  useEffect(() => {
    if ('Notification' in window) {
      Notification.requestPermission().then(permission => {
        setHasNotificationPermission(permission === 'granted');
      });
    }
    
    // Enable audio after first user interaction
    const enableAudio = () => {
      setUserHasInteracted(true);
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };
    
    document.addEventListener('click', enableAudio);
    document.addEventListener('keydown', enableAudio);
    
    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };
  }, []);

  // Real-time alert delivery: connects to the backend's per-user alert socket
  // so detections pop up immediately instead of waiting for the next poll.
  useEffect(() => {
    if (!userEmail) return;

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      ws = new WebSocket(`${protocol}//${window.location.host}/ws/alerts/${encodeURIComponent(userEmail)}`);

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type !== 'new_alert') return;

          const data = payload.data || {};
          const priority = (data.priority || 'medium').toLowerCase();
          const id = data.alert_id || `ws-${Date.now()}`;

          const notificationType: PopupNotification['type'] = (priority === 'critical' || priority === 'high') ? 'error' : 'warning';
          setNotifications(prev => [{
            id,
            title: `${(data.type || 'detection').replace(/_/g, ' ').toUpperCase()} - ${data.camera_name || 'Camera'}`,
            message: data.message || 'New detection event',
            type: notificationType,
            timestamp: new Date()
          }, ...prev].slice(0, 5));

          setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 12000);

          if (hasNotificationPermission && document.visibilityState !== 'visible') {
            try {
              new Notification(data.camera_name ? `${data.camera_name} Alert` : 'New Alert', {
                body: data.message || '',
                tag: id
              });
            } catch (e) { /* notification API unsupported in this context */ }
          }

          if (userHasInteracted) {
            try {
              const audio = new Audio('/notification-beep.wav');
              audio.volume = 0.5;
              audio.play().catch(() => {});
            } catch (e) { /* audio playback not available */ }
          }
        } catch (e) {
          console.error('Failed to parse real-time alert message:', e);
        }
      };

      ws.onclose = () => {
        if (!cancelled) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => {
        ws?.close();
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [userEmail, hasNotificationPermission, userHasInteracted]);

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ notifications, removeNotification }}>
      {children}
      
      {/* Global Popup Notifications */}
      <div className="fixed top-4 right-4 z-[9999] space-y-3 pointer-events-none">
        <div className="pointer-events-auto space-y-3">
        {notifications.map((notification) => {
          const getIcon = () => {
            if (notification.type === 'error') return '🔥';
            if (notification.type === 'warning') return '⚠️';
            if (notification.type === 'success') return '✅';
            return '🔔';
          };
          
          const getBorderColor = () => {
            if (notification.type === 'error') return 'border-red-500/40';
            if (notification.type === 'warning') return 'border-amber-500/40';
            if (notification.type === 'success') return 'border-emerald-500/40';
            return 'border-blue-500/40';
          };

          const getGlow = () => {
            if (notification.type === 'error') return 'shadow-[0_0_30px_rgba(239,68,68,0.15)]';
            if (notification.type === 'warning') return 'shadow-[0_0_30px_rgba(245,158,11,0.15)]';
            if (notification.type === 'success') return 'shadow-[0_0_30px_rgba(16,185,129,0.15)]';
            return 'shadow-[0_0_30px_rgba(59,130,246,0.15)]';
          };

          return (
            <div
              key={notification.id}
              className={`max-w-sm w-full bg-[#0d0f17]/95 backdrop-blur-2xl ${getBorderColor()} ${getGlow()} rounded-2xl border p-4 cursor-pointer hover:bg-white/[0.04] transition-all`}
              style={{
                animation: 'slideInRight 0.3s ease-out',
                zIndex: 10000,
                pointerEvents: 'auto'
              }}
              onClick={() => {
                navigate('/alerts');
                removeNotification(notification.id);
              }}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 text-2xl mr-3">
                  {getIcon()}
                </div>
                <div className="flex-1">
                  <p className="text-xs font-black text-white uppercase tracking-tight">
                    {notification.title}
                  </p>
                  <p className="mt-1 text-xs text-blue-100/60 font-light leading-relaxed">
                    {notification.message}
                  </p>
                  <p className="mt-1 text-[10px] text-blue-100/20 font-mono">
                    {notification.timestamp.toLocaleTimeString()}
                  </p>
                  <p className="mt-1 text-[10px] text-blue-400 font-bold uppercase tracking-widest">
                    Click to view alerts
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <button
                    className="bg-white/5 rounded-full p-1.5 text-blue-100/40 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNotification(notification.id);
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      </div>
      
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </NotificationContext.Provider>
  );
};